import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DollarSign,
  TrendingUp,
  Users,
  Wrench,
  Calendar,
  Award,
  BarChart3,
  FileText,
  Download,
  Search,
  Loader2,
  Trophy,
  Target,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Filter
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [filters, setFilters] = useState({
    technician_id: 'all',
    service_type: 'all',
    period: 'month',
    date_from: '',
    date_to: ''
  });
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('resumen');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.technician_id !== 'all') params.append('technician_id', filters.technician_id);
      if (filters.service_type !== 'all') params.append('service_type', filters.service_type);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      params.append('period', filters.period);

      const [reportsRes, techsRes] = await Promise.all([
        axios.get(`${API}/reports/production?${params.toString()}`),
        axios.get(`${API}/technicians`)
      ]);

      setData(reportsRes.data);
      setTechnicians(techsRes.data);
    } catch (error) {
      toast.error('Error al cargar reportes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleQuickFilter = (period) => {
    const today = new Date();
    let dateFrom = '';
    let dateTo = format(today, 'yyyy-MM-dd');

    switch (period) {
      case 'week':
        dateFrom = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        break;
      case 'month':
        dateFrom = format(startOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'year':
        dateFrom = format(startOfYear(today), 'yyyy-MM-dd');
        break;
      default:
        dateFrom = '';
        dateTo = '';
    }

    setFilters({ ...filters, period, date_from: dateFrom, date_to: dateTo });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const exportToCSV = (dataArray, filename) => {
    if (!dataArray || dataArray.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    const headers = Object.keys(dataArray[0]);
    const csvContent = [
      headers.join(','),
      ...dataArray.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('Archivo exportado');
  };

  const filteredJobs = data?.jobs?.filter(job => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      job.client_name?.toLowerCase().includes(searchLower) ||
      job.technician_name?.toLowerCase().includes(searchLower) ||
      job.folio?.toLowerCase().includes(searchLower) ||
      job.folio_nota?.toLowerCase().includes(searchLower) ||
      job.description?.toLowerCase().includes(searchLower) ||
      job.service_type?.toLowerCase().includes(searchLower)
    );
  }) || [];

  const serviceTypes = [...new Set(data?.jobs?.map(j => j.service_type).filter(Boolean) || [])];

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Control de Producción</h2>
          <p className="text-muted-foreground text-sm">Análisis de ingresos y rendimiento</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportToCSV(data?.jobs, 'trabajos')}>
            <Download className="h-4 w-4 mr-2" />
            Exportar Trabajos
          </Button>
          <Button variant="outline" onClick={() => exportToCSV(data?.by_technician, 'tecnicos')}>
            <Download className="h-4 w-4 mr-2" />
            Exportar Técnicos
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Período</Label>
              <div className="flex gap-1">
                <Button 
                  size="sm" 
                  variant={filters.period === 'week' ? 'default' : 'outline'}
                  onClick={() => handleQuickFilter('week')}
                >
                  Semana
                </Button>
                <Button 
                  size="sm" 
                  variant={filters.period === 'month' ? 'default' : 'outline'}
                  onClick={() => handleQuickFilter('month')}
                >
                  Mes
                </Button>
                <Button 
                  size="sm" 
                  variant={filters.period === 'year' ? 'default' : 'outline'}
                  onClick={() => handleQuickFilter('year')}
                >
                  Año
                </Button>
              </div>
            </div>
            <div className="space-y-1 min-w-[150px]">
              <Label className="text-xs">Técnico</Label>
              <Select value={filters.technician_id} onValueChange={(v) => setFilters({ ...filters, technician_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los técnicos</SelectItem>
                  {technicians.filter(t => t.is_active).map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 min-w-[150px]">
              <Label className="text-xs">Tipo de Servicio</Label>
              <Select value={filters.service_type} onValueChange={(v) => setFilters({ ...filters, service_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los servicios</SelectItem>
                  {serviceTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Desde</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value, period: 'custom' })}
                className="w-[140px]"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Hasta</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value, period: 'custom' })}
                className="w-[140px]"
              />
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setFilters({ technician_id: 'all', service_type: 'all', period: 'month', date_from: '', date_to: '' })}
            >
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tarjetas de Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Calendar className="h-8 w-8 opacity-80" />
              <Badge className="bg-white/20 text-white">Semana</Badge>
            </div>
            <p className="text-2xl font-bold mt-2">{formatCurrency(data?.summary?.week?.total)}</p>
            <p className="text-sm opacity-80">{data?.summary?.week?.count || 0} trabajos</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <TrendingUp className="h-8 w-8 opacity-80" />
              <Badge className="bg-white/20 text-white">Mes</Badge>
            </div>
            <p className="text-2xl font-bold mt-2">{formatCurrency(data?.summary?.month?.total)}</p>
            <p className="text-sm opacity-80">{data?.summary?.month?.count || 0} trabajos</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <BarChart3 className="h-8 w-8 opacity-80" />
              <Badge className="bg-white/20 text-white">Año</Badge>
            </div>
            <p className="text-2xl font-bold mt-2">{formatCurrency(data?.summary?.year?.total)}</p>
            <p className="text-sm opacity-80">{data?.summary?.year?.count || 0} trabajos</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Target className="h-8 w-8 opacity-80" />
              <Badge className="bg-white/20 text-white">Promedio</Badge>
            </div>
            <p className="text-2xl font-bold mt-2">{formatCurrency(data?.summary?.avg_ticket)}</p>
            <p className="text-sm opacity-80">por trabajo</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Trophy className="h-8 w-8 opacity-80" />
              <Badge className="bg-white/20 text-white">Top Técnico</Badge>
            </div>
            <p className="text-lg font-bold mt-2 truncate">{data?.summary?.best_technician_month?.name || '-'}</p>
            <p className="text-sm opacity-80">{formatCurrency(data?.summary?.best_technician_month?.total)} este mes</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-pink-500 to-pink-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Award className="h-8 w-8 opacity-80" />
              <Badge className="bg-white/20 text-white">Top Servicio</Badge>
            </div>
            <p className="text-lg font-bold mt-2 truncate">{data?.summary?.best_service_month?.type || '-'}</p>
            <p className="text-sm opacity-80">{formatCurrency(data?.summary?.best_service_month?.total)} este mes</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs de contenido */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-[500px]">
          <TabsTrigger value="resumen">Resumen</TabsTrigger>
          <TabsTrigger value="tecnicos">Por Técnico</TabsTrigger>
          <TabsTrigger value="servicios">Por Servicio</TabsTrigger>
          <TabsTrigger value="trabajos">Trabajos</TabsTrigger>
        </TabsList>

        {/* Tab Resumen */}
        <TabsContent value="resumen" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfica de Ingresos Mensuales */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Ingresos por Mes
                </CardTitle>
                <CardDescription>Últimos 12 meses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data?.monthly_income || []}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip 
                        formatter={(value) => [formatCurrency(value), 'Total']}
                        labelFormatter={(label) => `Mes: ${label}`}
                      />
                      <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Gráfica por Técnico */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Ingresos por Técnico
                </CardTitle>
                <CardDescription>Total histórico</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data?.by_technician?.slice(0, 8) || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip formatter={(value) => [formatCurrency(value), 'Total']} />
                      <Bar dataKey="total_amount" fill="#10b981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Gráfica por Tipo de Servicio */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="h-5 w-5" />
                  Distribución por Servicio
                </CardTitle>
                <CardDescription>Porcentaje de ingresos</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={data?.by_service_type?.slice(0, 8) || []}
                        dataKey="total_amount"
                        nameKey="type"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ type, percent }) => `${type}: ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {(data?.by_service_type || []).map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Rankings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  Rankings
                </CardTitle>
                <CardDescription>Mejores resultados por período</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <Users className="h-4 w-4" /> Mejor Técnico
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center p-2 bg-blue-50 dark:bg-blue-950/30 rounded">
                        <span className="text-sm">Semana</span>
                        <div className="text-right">
                          <p className="font-medium text-sm">{data?.summary?.best_technician_week?.name || '-'}</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(data?.summary?.best_technician_week?.total)}</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-green-50 dark:bg-green-950/30 rounded">
                        <span className="text-sm">Mes</span>
                        <div className="text-right">
                          <p className="font-medium text-sm">{data?.summary?.best_technician_month?.name || '-'}</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(data?.summary?.best_technician_month?.total)}</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-purple-50 dark:bg-purple-950/30 rounded">
                        <span className="text-sm">Año</span>
                        <div className="text-right">
                          <p className="font-medium text-sm">{data?.summary?.best_technician_year?.name || '-'}</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(data?.summary?.best_technician_year?.total)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <Wrench className="h-4 w-4" /> Mejor Servicio
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center p-2 bg-blue-50 dark:bg-blue-950/30 rounded">
                        <span className="text-sm">Semana</span>
                        <div className="text-right">
                          <p className="font-medium text-sm">{data?.summary?.best_service_week?.type || '-'}</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(data?.summary?.best_service_week?.total)}</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-green-50 dark:bg-green-950/30 rounded">
                        <span className="text-sm">Mes</span>
                        <div className="text-right">
                          <p className="font-medium text-sm">{data?.summary?.best_service_month?.type || '-'}</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(data?.summary?.best_service_month?.total)}</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-purple-50 dark:bg-purple-950/30 rounded">
                        <span className="text-sm">Año</span>
                        <div className="text-right">
                          <p className="font-medium text-sm">{data?.summary?.best_service_year?.type || '-'}</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(data?.summary?.best_service_year?.total)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Por Técnico */}
        <TabsContent value="tecnicos">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Reporte por Técnico
              </CardTitle>
              <CardDescription>Rendimiento y producción de cada técnico</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Técnico</TableHead>
                      <TableHead className="text-right">Trabajos</TableHead>
                      <TableHead className="text-right">Total Generado</TableHead>
                      <TableHead className="text-right">Anticipos</TableHead>
                      <TableHead className="text-right">Promedio</TableHead>
                      <TableHead className="text-right">Semana</TableHead>
                      <TableHead className="text-right">Mes</TableHead>
                      <TableHead className="text-right">Año</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.by_technician?.map((tech, index) => (
                      <TableRow key={tech.id || index}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {index < 3 && (
                              <Badge variant={index === 0 ? 'default' : 'secondary'} className="w-6 h-6 p-0 flex items-center justify-center">
                                {index + 1}
                              </Badge>
                            )}
                            <span className="font-medium">{tech.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{tech.total_jobs}</TableCell>
                        <TableCell className="text-right font-semibold text-green-600 dark:text-green-400">
                          {formatCurrency(tech.total_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {tech.anticipos_cobrados > 0 ? (
                            <div>
                              <p className="text-orange-600 dark:text-orange-400 font-medium">
                                {formatCurrency(tech.anticipos_cobrados)}
                              </p>
                              <p className="text-xs text-muted-foreground">{tech.visitas_con_anticipo} visitas</p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(tech.avg_per_job)}</TableCell>
                        <TableCell className="text-right">
                          <div>
                            <p className="font-medium">{formatCurrency(tech.week_total)}</p>
                            <p className="text-xs text-muted-foreground">{tech.week_count} trabajos</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div>
                            <p className="font-medium">{formatCurrency(tech.month_total)}</p>
                            <p className="text-xs text-muted-foreground">{tech.month_count} trabajos</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div>
                            <p className="font-medium">{formatCurrency(tech.year_total)}</p>
                            <p className="text-xs text-muted-foreground">{tech.year_count} trabajos</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!data?.by_technician || data.by_technician.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No hay datos de técnicos
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Por Servicio */}
        <TabsContent value="servicios">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Reporte por Tipo de Servicio
              </CardTitle>
              <CardDescription>Qué servicios generan más dinero</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo de Servicio</TableHead>
                      <TableHead className="text-right">Trabajos</TableHead>
                      <TableHead className="text-right">Total Generado</TableHead>
                      <TableHead className="text-right">Promedio</TableHead>
                      <TableHead className="text-right">Semana</TableHead>
                      <TableHead className="text-right">Mes</TableHead>
                      <TableHead className="text-right">Año</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.by_service_type?.map((svc, index) => (
                      <TableRow key={svc.type || index}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {index < 3 && (
                              <Badge variant={index === 0 ? 'default' : 'secondary'} className="w-6 h-6 p-0 flex items-center justify-center">
                                {index + 1}
                              </Badge>
                            )}
                            <span className="font-medium">{svc.type}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{svc.total_jobs}</TableCell>
                        <TableCell className="text-right font-semibold text-green-600 dark:text-green-400">
                          {formatCurrency(svc.total_amount)}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(svc.avg_per_job)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(svc.week_total)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(svc.month_total)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(svc.year_total)}</TableCell>
                      </TableRow>
                    ))}
                    {(!data?.by_service_type || data.by_service_type.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No hay datos de servicios
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Trabajos */}
        <TabsContent value="trabajos">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Listado de Trabajos Finalizados
              </CardTitle>
              <CardDescription>
                {filteredJobs.length} trabajos encontrados
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por cliente, técnico, folio, descripción..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="overflow-x-auto max-h-[500px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Folio</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Técnico Final</TableHead>
                      <TableHead>Servicio</TableHead>
                      <TableHead className="text-right">Costo Final</TableHead>
                      <TableHead>Cobros por Visita</TableHead>
                      <TableHead>Folio Nota</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredJobs.map((job) => (
                      <TableRow key={job.id}>
                        <TableCell className="text-sm">
                          {job.service_date ? format(parseISO(job.service_date), 'dd/MM/yy') : '-'}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{job.folio}</TableCell>
                        <TableCell className="font-medium">{job.client_name}</TableCell>
                        <TableCell>
                          <div>
                            <p>{job.technician_name || '-'}</p>
                            {job.num_visitas > 1 && (
                              <Badge variant="outline" className="text-xs mt-1">
                                {job.num_visitas} visitas
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{job.service_type}</TableCell>
                        <TableCell className="text-right font-semibold text-green-600 dark:text-green-400">
                          {formatCurrency(job.monto_cobrado)}
                        </TableCell>
                        <TableCell>
                          {job.cobros_por_tecnico && job.cobros_por_tecnico.length > 0 ? (
                            <div className="space-y-1">
                              {job.cobros_por_tecnico.map((cobro, idx) => (
                                <div key={idx} className="text-xs">
                                  <span className="text-muted-foreground">V{cobro.visita}:</span>{' '}
                                  <span className="font-medium">{cobro.tecnico}</span>
                                  {cobro.anticipo > 0 && (
                                    <span className="text-orange-600 ml-1">
                                      (Ant: {formatCurrency(cobro.anticipo)})
                                    </span>
                                  )}
                                  {cobro.monto_final > 0 && (
                                    <span className="text-green-600 ml-1">
                                      (Final: {formatCurrency(cobro.monto_final)})
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">1 visita</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-blue-600 dark:text-blue-400">
                          {job.folio_nota || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredJobs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No se encontraron trabajos
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
